import { useEffect, useState } from "react";
import { Download, Share, X, Plus } from "lucide-react";

/**
 * PWA install nudge.
 *
 * Android / Chrome / Edge: listens for the `beforeinstallprompt` event, shows a small toast
 * that lets the user install with one tap. We defer the prompt until the user taps our button
 * (so it's user-gesture-driven — Chrome's requirement).
 *
 * iOS Safari: no install event exists — Apple only supports "Add to Home Screen" via the
 * share sheet. So when we detect iOS Safari (and not already installed), we show a friendly
 * one-time card explaining the manual steps.
 *
 * Both prompts are dismissible; we remember the dismissal in localStorage so we don't nag
 * users on every visit. They can still install manually from the browser menu.
 */
const DISMISS_KEY = "hf.installPromptDismissedAt";
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function isRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return (Date.now() - Number(raw)) < DISMISS_TTL_MS;
  } catch { return false; }
}

function markDismissed() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
}

function isAlreadyInstalled() {
  if (typeof window === "undefined") return false;
  const displayModeStandalone = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  // iOS-specific: Safari sets navigator.standalone when running from home-screen
  const iosStandalone = window.navigator && window.navigator.standalone === true;
  return displayModeStandalone || iosStandalone;
}

export default function PwaInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null); // Chrome / Android
  const [showIOS, setShowIOS] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isAlreadyInstalled() || isRecentlyDismissed()) return undefined;

    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isSafari = /Safari/i.test(navigator.userAgent) && !/CriOS|FxiOS|EdgiOS|OPiOS|Chrome/i.test(navigator.userAgent);
    const iosSafari = isIOS && isSafari;

    if (iosSafari) {
      // Delay a couple of seconds so we don't compete with page load / auth prompts
      const t = setTimeout(() => { setShowIOS(true); setVisible(true); }, 3000);
      return () => clearTimeout(t);
    }

    const onBip = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    // Optional: Chrome fires `appinstalled` when the user completes the install
    const onInstalled = () => setVisible(false);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    markDismissed();
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "dismissed") markDismissed();
    } catch { /* ignore */ }
    setDeferredPrompt(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed z-50 bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm"
      data-testid="pwa-install-prompt"
    >
      <div className="brand-card p-4 shadow-2xl border-2 border-primary/20 bg-white/95 backdrop-blur relative">
        <button
          type="button"
          onClick={dismiss}
          className="absolute top-2 right-2 w-8 h-8 rounded-full text-[#7A7D96] hover:bg-[#3D405B]/5 flex items-center justify-center transition"
          aria-label="Dismiss install prompt"
          data-testid="install-dismiss-btn"
        >
          <X size={16} />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <div className="w-10 h-10 rounded-2xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
            <Download size={18} />
          </div>
          <div className="min-w-0">
            <div className="font-extrabold text-[#3D405B] text-sm leading-tight">Install Hellofoundation</div>
            <p className="text-xs text-[#7A7D96] mt-1 leading-relaxed">
              {showIOS
                ? "Add us to your home screen for faster access, offline journaling and session reminders."
                : "Get faster access, offline journaling and session reminders. Free — no download from a store."}
            </p>

            {showIOS ? (
              <ol className="mt-3 space-y-1.5 text-xs text-[#3D405B]">
                <li className="flex items-center gap-1.5">
                  <span className="w-5 h-5 rounded-full bg-primary/15 text-primary font-extrabold text-[10px] flex items-center justify-center shrink-0">1</span>
                  Tap <Share size={12} className="inline mx-0.5 -mt-0.5" /> Share
                </li>
                <li className="flex items-center gap-1.5">
                  <span className="w-5 h-5 rounded-full bg-primary/15 text-primary font-extrabold text-[10px] flex items-center justify-center shrink-0">2</span>
                  Choose <Plus size={12} className="inline mx-0.5 -mt-0.5" /> Add to Home Screen
                </li>
              </ol>
            ) : (
              <button
                type="button"
                onClick={install}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-white text-xs font-extrabold shadow hover:-translate-y-0.5 transition"
                data-testid="install-cta-btn"
              >
                <Download size={13} /> Install app
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
